import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, Shield } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';

import { BACKEND_URL } from '../config/api';

const defaultPrivacyContent = `
<p class="lead">Last updated: February 2026</p>

<p><strong>OnSiteHand is operated by ONSITEHAND LIMITED. ONSITEHAND LIMITED is the data controller for personal data processed through the OnSiteHand platform, unless stated otherwise. We are registered in England and Wales under company number 17186065, with our registered office at Unit A449, 4-6 Greatorex Street, London, E1 5NF, United Kingdom.</strong></p>

<p>This Privacy Policy explains how ONSITEHAND LIMITED ("we", "us", "our") collects, uses and protects your personal data when you use our platform.</p>

<p>We keep certain fields strictly private and never display them to other users. These include your full home postcode, phone number, credential numbers, UTR (tradesperson tax reference), VAT number where stored, and uploaded verification documents.</p>

<h2>1. Information We Collect</h2>

<h3>Information You Provide</h3>
<ul>
  <li><strong>Account information:</strong> Name, email address, phone number, password.</li>
  <li><strong>Profile information:</strong> Trade/profession, skills, experience, qualifications, daily rate, work areas, availability, transport type.</li>
  <li><strong>Verification documents:</strong> ID documents, certificates, insurance documents, company registration documents (where you choose to submit them).</li>
  <li><strong>Portfolio:</strong> Photos and descriptions of your work.</li>
  <li><strong>Communications:</strong> Messages sent through the platform, support enquiries.</li>
  <li><strong>Worker contact preference:</strong> If you are a tradesperson, you choose whether approved employers may be allowed to request your phone number through the platform, or whether contact is restricted to in-app messages only. Your preference is stored on your profile and may be updated at any time.</li>
</ul>

<h3>Information Collected Automatically</h3>
<ul>
  <li>Device and browser information.</li>
  <li>IP address and location data.</li>
  <li>Usage data (pages visited, features used, time spent).</li>
  <li>Cookies and similar technologies (see our <a href="/cookies">Cookie Policy</a>).</li>
</ul>

<h2>2. How We Use Your Information</h2>
<ul>
  <li><strong>Provide the service:</strong> connect employers with tradespeople and enable in-platform messaging.</li>
  <li><strong>Improve the platform:</strong> analyse usage, develop features, fix issues.</li>
  <li><strong>Communicate with you:</strong> service notifications, support responses, important updates.</li>
  <li><strong>Verification evidence review:</strong> where you submit documents, review them to decide whether a verification badge can be shown next to the specific credential.</li>
  <li><strong>Safety and security:</strong> detect and prevent fraud and abuse, enforce our Terms, keep accounts secure.</li>
  <li><strong>Legal compliance:</strong> meet our legal obligations and respond to lawful requests.</li>
</ul>

<h2>3. Phone-reveal access logging</h2>
<p>Where a tradesperson has set their contact preference to allow approved employers to request their phone number, and an approved registered employer successfully requests that number through the platform, we record the request. The record contains the employer's user id and email, the worker's user id, the timestamp, and the contact preference that was in effect at the time. We use this record so that the worker and OnSiteHand administrators can see who accessed the number and when, and for security, abuse-prevention and platform-operation purposes. We do not use this log for marketing.</p>

<h2>4. Legal basis for processing (UK GDPR)</h2>
<ul>
  <li><strong>Contract:</strong> to provide the services you have signed up for.</li>
  <li><strong>Legitimate interests:</strong> to keep the platform safe, prevent fraud and abuse, and improve the service — including recording phone-reveal access events.</li>
  <li><strong>Legal obligation:</strong> where processing is required by law.</li>
  <li><strong>Consent:</strong> where you have opted in (for example to allow approved employers to request your phone number, or to receive marketing emails).</li>
</ul>

<h2>5. Retention</h2>
<p>We keep personal data for as long as reasonably necessary for the purpose we collected it, and for as long as needed to meet our legal, accounting and security obligations. Where no fixed retention period is defined for a specific category, we review it periodically and keep it only as long as there is a legitimate reason to do so. <em>Specific retention periods are reviewed by a solicitor from time to time; where legal interpretation is required, professional legal advice should be sought.</em></p>

<h2>6. Information Sharing</h2>
<p>We share information only in limited situations, including with service providers who help us run the platform (for example infrastructure and email delivery) under appropriate contracts, and where we are required to share information by law. We do not sell your personal data.</p>

<h2>7. Your Rights</h2>
<p>Under UK data-protection law you have rights including access, rectification, erasure, restriction, portability and objection. To exercise any right, contact <a href="mailto:privacy@onsitehand.co.uk">privacy@onsitehand.co.uk</a>.</p>

<h2>8. Contact</h2>
<p>For privacy questions, contact <a href="mailto:privacy@onsitehand.co.uk">privacy@onsitehand.co.uk</a>. You also have the right to complain to the UK Information Commissioner's Office (<a href="https://ico.org.uk" target="_blank" rel="noopener">ico.org.uk</a>).</p>
`;

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/pages/privacy`);
      if (response.data.sections?.main?.content) {
        setContent(response.data.sections.main.content);
      }
    } catch (error) {
      // Fallback to defaults
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <Building2 className="h-6 w-6 text-orange-500" />
              <span className="text-xl font-bold text-slate-900">OnsiteHand</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary text-sm"
            data-testid="login-btn"
          >
            Sign In
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <Shield className="h-8 w-8 text-orange-500" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="page-title">
            Privacy Policy
          </h1>
          <p className="text-slate-600">Last updated: January 2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-slate-200 rounded w-1/3"></div>
              <div className="h-4 bg-slate-200 rounded w-full"></div>
              <div className="h-4 bg-slate-200 rounded w-full"></div>
              <div className="h-4 bg-slate-200 rounded w-2/3"></div>
            </div>
          ) : (
            <div 
              className="prose prose-slate max-w-none [&>h2]:text-xl [&>h2]:font-bold [&>h2]:text-slate-900 [&>h2]:mt-8 [&>h2]:mb-4 [&>h2:first-child]:mt-0 [&>p]:text-slate-600 [&>p]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:text-slate-600"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content || defaultPrivacyContent) }}
              data-testid="privacy-content"
            />
          )}
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Also see our{' '}
            <button onClick={() => navigate('/terms')} className="text-orange-500 hover:underline">
              Terms of Service
            </button>
            {' '}and{' '}
            <button onClick={() => navigate('/faq')} className="text-orange-500 hover:underline">
              FAQ
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
